import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'
import { isOwnerSource } from '../lib/interAgentTurn'
import { useChannelSubscription, type ChannelSubscription } from '../composables/useChannelSubscription'

export interface AgentChannelSummary {
  id: number
  name: string
  archived: boolean
  member_count: number
  member_thread_ids: string[]
  last_activity_at: string | null
  unread_count: number
}

export interface AgentChannelMessage {
  id: number
  sender_name: string
  source: 'ui_user' | 'mcp'
  sender_thread_id: string | null
  body: string
  created_at: string
}

// Admin store for the shared agent-channel feed (#1835): the channel list
// plus the open channel's messages, kept live by a per-channel ActionCable
// subscription (dropped and re-opened when the selection changes).
export const useAgentChannelsStore = defineStore('agentChannels', () => {
  const channels = ref<AgentChannelSummary[]>([])
  const messages = ref<AgentChannelMessage[]>([])
  const selectedId = ref<number | null>(null)
  const loadError = ref<string | null>(null)
  const channelsLoaded = ref(false)
  const loadingMessages = ref(false)
  let subscription: ChannelSubscription | null = null

  async function fetchChannels(): Promise<void> {
    try {
      const data = await apiGet<{ channels: AgentChannelSummary[] }>('/api/v1/agent/channels', {
        failureLabel: 'Failed to load channels',
      })
      channels.value = data.channels
      loadError.value = null
    } catch (e) {
      loadError.value = e instanceof Error ? e.message : String(e)
    } finally {
      channelsLoaded.value = true
    }
  }

  async function fetchMessages(id: number): Promise<void> {
    const data = await apiGet<{ messages: AgentChannelMessage[] }>(`/api/v1/agent/channels/${id}/messages`, {
      failureLabel: 'Failed to load channel messages',
    })
    if (selectedId.value === id) messages.value = data.messages
  }

  function applyBroadcast(id: number, message: AgentChannelMessage): void {
    if (selectedId.value !== id || messages.value.some((m) => m.id === message.id)) return
    messages.value = [...messages.value, message]
    const channel = channels.value.find((c) => c.id === id)
    if (channel) channel.last_activity_at = message.created_at
    // Someone else's message landing in the channel being read isn't unread.
    if (!isOwnerSource(message.source)) void markRead(id)
  }

  // Advance the owner's read pointer so the unread indicator clears (#1837).
  // Best-effort: a failed mark-read must not break the feed.
  async function markRead(id: number): Promise<void> {
    try {
      const data = await apiMutate<{ channel: AgentChannelSummary }>(`${base}/${id}/read`, {
        method: 'POST',
        failureLabel: 'Failed to mark channel read',
      })
      channels.value = channels.value.map((c) => (c.id === id ? { ...c, unread_count: data.channel.unread_count } : c))
    } catch {
      // The next list fetch re-syncs the count.
    }
  }

  async function select(id: number): Promise<void> {
    subscription?.unsubscribe()
    selectedId.value = id
    messages.value = []
    subscription = useChannelSubscription<AgentChannelMessage>(
      { channel: 'AgentChannelFeedChannel', params: { channel_id: id } },
      {
        received: (message) => applyBroadcast(id, message),
        // A WS drop can miss broadcasts; re-pull the page to catch up.
        refetchOnReconnect: () => void fetchMessages(id).catch(() => {}),
      },
    )
    loadingMessages.value = true
    try {
      await fetchMessages(id)
      loadError.value = null
      await markRead(id)
    } catch (e) {
      loadError.value = e instanceof Error ? e.message : String(e)
    } finally {
      if (selectedId.value === id) loadingMessages.value = false
    }
  }

  function close(): void {
    subscription?.unsubscribe()
    subscription = null
    selectedId.value = null
    messages.value = []
  }

  const base = '/api/v1/agent/channels'

  async function createChannel(name: string): Promise<AgentChannelSummary> {
    const data = await apiMutate<{ channel: AgentChannelSummary }>(base, {
      method: 'POST',
      body: { agent_channel: { name } },
      failureLabel: 'Failed to create channel',
    })
    channels.value = [...channels.value, data.channel].toSorted((a, b) => a.name.localeCompare(b.name))
    return data.channel
  }

  async function updateChannel(id: number, attrs: { name?: string; archived?: boolean }): Promise<void> {
    const data = await apiMutate<{ channel: AgentChannelSummary }>(`${base}/${id}`, {
      method: 'PATCH',
      body: { agent_channel: attrs },
      failureLabel: 'Failed to update channel',
    })
    channels.value = channels.value.map((c) => (c.id === id ? data.channel : c))
  }

  async function deleteChannel(id: number): Promise<void> {
    await apiMutate(`${base}/${id}`, { method: 'DELETE', failureLabel: 'Failed to delete channel' })
    channels.value = channels.value.filter((c) => c.id !== id)
    if (selectedId.value === id) close()
  }

  // Resolves to how many member sessions the post woke (0 = none live).
  async function postMessage(id: number, body: string): Promise<number> {
    const data = await apiMutate<{ message: AgentChannelMessage; woken_sessions?: number }>(`${base}/${id}/messages`, {
      method: 'POST',
      body: { body },
      failureLabel: 'Failed to post message',
    })
    applyBroadcast(id, data.message)
    return data.woken_sessions ?? 0
  }

  async function addMember(id: number, threadId: string): Promise<void> {
    await apiMutate(`${base}/${id}/members`, {
      method: 'POST',
      body: { thread_id: threadId },
      failureLabel: 'Failed to add member',
    })
    await fetchChannels()
  }

  async function removeMember(id: number, threadId: string): Promise<void> {
    await apiMutate(`${base}/${id}/members/${encodeURIComponent(threadId)}`, {
      method: 'DELETE',
      failureLabel: 'Failed to remove member',
    })
    await fetchChannels()
  }

  return {
    channels,
    messages,
    selectedId,
    loadError,
    channelsLoaded,
    loadingMessages,
    fetchChannels,
    select,
    markRead,
    close,
    createChannel,
    updateChannel,
    deleteChannel,
    postMessage,
    addMember,
    removeMember,
  }
})
